import { ApiRequestError, type SavedConfig } from "@cfg/api-contract";
import { compareSemver } from "@cfg/design-sdk";
import {
  ConfiguratorProvider,
  FitTagInput,
  IssueList,
  ParamForm,
  PresetBar,
  Viewport,
  WidgetRegistryProvider,
  buildConfiguratorSearch,
  getDraft,
  parseConfiguratorUrl,
  resolveShareUrl,
  saveDraft,
  scrollBehavior,

  useChanged,
  useDesign,
  useExport,
  useFlash,
  useFocusParam,
  useMesh,
  useMigrate,
  useParams,
  type Draft,
  type FitTagValue,
} from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams as useRouteParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { getKernelClient } from "../kernel.ts";
import { defaultConfigName, displayTitle } from "../titles.ts";
import { DefaultConfiguratorFrame, DefaultEmptyState } from "./defaults.tsx";
import { DisplayNameForm } from "./DisplayNameForm.tsx";

interface Opened {
  designId: string;
  version: string;
  bundleUrl: string;
  latest: string;
  deprecated: boolean;
  params: Record<string, unknown>;
  /** Saved config (server) or draft (local) this was opened from. */
  saved?: { kind: "config"; config: SavedConfig } | { kind: "draft"; draft: Draft };
}

/**
 * Configurator copy that is not (yet) in brand-sdk's `copyDefaults`: brands override any of these
 * ids in `config.copy` like the typed keys; the fallback is the neutral default.
 */
const extraCopy = {
  "configurator.preparing": "Preparing…",
  "configurator.exporting": "Exporting…",
  "configurator.saving": "Saving…",
  "configurator.done.export": "✓ Downloaded",
  "configurator.done.save": "✓ Saved",
  "configurator.done.share": "✓ Link copied",
  "configurator.done.publish": "✓ Published",
  "configurator.done.unpublish": "✓ Made private",
  "configurator.blocked.one": "Fix 1 print issue to download ↑",
  "configurator.blocked.other": "Fix {count} print issues to download ↑",
  "configurator.saveHeading": "Save & share",
  "configurator.saveJump": "Save ↓",
  "configurator.nameLabel": "Name",
  "configurator.namePlaceholder": "Name this configuration",
  "configurator.notesLabel": "Notes",
  "configurator.viewport": "3D preview of {title}",
  "configurator.busy": "building…",
  "configurator.clampedMin": "Min {value}",
  "configurator.clampedMax": "Max {value}",
  "configurator.loading": "Loading design…",
  "configurator.live.exported": "Downloaded {filename} ({size})",
  "configurator.live.linkCopied": "Link copied: {url}",
  "configurator.live.linkReady": "Copy this link: {url}",
} as const;
type ExtraCopyKey = keyof typeof extraCopy;

function useExtraCopy() {
  const { brand } = useApp();
  return useCallback(
    (key: ExtraCopyKey, vars: Record<string, string | number> = {}) => {
      let s = brand.config.copy[key] ?? extraCopy[key];
      for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
      return s;
    },
    [brand],
  );
}

/** Placeholder rows for the parameter panel while the design loads. */
function PanelSkeleton() {
  return (
    <div className="cfg-skeleton" aria-hidden>
      <div className="cfg-skeleton-block" style={{ height: "1.4em", width: "45%" }} />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="cfg-skeleton-block" style={{ height: "3.2em" }} />
      ))}
      <div className="cfg-skeleton-block" style={{ height: "2.8em", marginTop: "0.5em" }} />
    </div>
  );
}

/** The configurator frame (stage + panel placeholders) shown before the design data arrives. */
function ConfiguratorSkeleton() {
  const { brand } = useApp();
  const t = useExtraCopy();
  const Frame = brand.slots?.ConfiguratorFrame ?? DefaultConfiguratorFrame;
  return (
    <section className="page page-configurator is-loading" aria-busy="true">
      <header className="page-head configurator-head">
        <div>
          <h1>
            <span className="cfg-visually-hidden">{t("configurator.loading")}</span>
            <span className="cfg-skeleton-block" aria-hidden style={{ display: "inline-block", width: "12ch", height: "1em" }} />
          </h1>
        </div>
      </header>
      <Frame>
        <div className="configurator-layout">
          <div className="configurator-stage">
            <div className="cfg-viewport cfg-viewport-skeleton" aria-hidden />
            <div className="stage-stats">
              <span>{t("configurator.loading")}</span>
            </div>
          </div>
          <aside className="configurator-panel">
            <PanelSkeleton />
          </aside>
        </div>
      </Frame>
    </section>
  );
}

function StatChip({ value, kind }: { value: string; kind: string }) {
  const changed = useChanged(value);
  return (
    <span className={`stage-stat stage-stat-${kind}`} data-changed={changed || undefined}>
      {value}
    </span>
  );
}

export function ConfiguratorPage() {
  const { id = "" } = useRouteParams();
  const location = useLocation();
  const { registry, api, brand } = useApp();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const urlState = useMemo(() => parseConfiguratorUrl(location.search), [location.search]);

  const opened = useQuery({
    queryKey: ["open", id, urlState.version, urlState.configId],
    queryFn: async (): Promise<Opened | null> => {
      let params = urlState.params;
      let version = urlState.version;
      let saved: Opened["saved"];
      if (urlState.configId) {
        const draft = await getDraft(urlState.configId);
        if (draft) {
          saved = { kind: "draft", draft };
          params = draft.params;
          version = draft.version;
        } else if (api) {
          const { config } = await api.getConfig(urlState.configId);
          saved = { kind: "config", config };
          params = config.params;
          version = config.version;
        }
      }
      const r = await registry.resolve(id, version);
      if (!r) return null;
      // Params handed over by the "update to <version>" flow.
      const handoverKey = `cfg-migrated:${id}@${r.version}`;
      const handover = sessionStorage.getItem(handoverKey);
      if (handover) {
        sessionStorage.removeItem(handoverKey);
        try {
          params = JSON.parse(handover) as Record<string, unknown>;
        } catch {
          /* ignore */
        }
      }
      return { designId: id, version: r.version, bundleUrl: r.bundleUrl, latest: r.latest, deprecated: r.deprecated, params, saved };
    },
  });

  if (opened.isLoading) return <ConfiguratorSkeleton />;
  if (opened.isError) return <Empty title="Could not open this design" body={String(opened.error)} />;
  if (!opened.data) return <Empty title="Design not found" body={`${id}${urlState.version ? `@${urlState.version}` : ""} is not in this catalog.`} />;
  return <ConfiguratorScreen key={`${opened.data.designId}@${opened.data.version}:${urlState.configId ?? ""}`} opened={opened.data} />;
}

function ConfiguratorScreen({ opened }: { opened: Opened }) {
  const { brand, analytics } = useApp();
  const t = useExtraCopy();
  const widgetText = useMemo(() => ({ clampedMin: t("configurator.clampedMin"), clampedMax: t("configurator.clampedMax") }), [t]);
  const navigate = useNavigate();
  const client = getKernelClient();
  const key = `${opened.designId}@${opened.version}`;
  const defaultsRef = useRef<Record<string, unknown>>({});

  useEffect(() => analytics.track("view", opened.designId, opened.version), [analytics, opened.designId, opened.version]);

  const onParamsChange = useCallback(
    (params: Record<string, unknown>) => {
      const search = buildConfiguratorSearch(
        { designId: undefined, version: opened.version, params, configId: opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.id : opened.saved.config.id) : undefined },
        defaultsRef.current,
      );
      if (search !== window.location.search) window.history.replaceState(null, "", `${window.location.pathname}${search}`);
      analytics.track("configure", opened.designId, opened.version);
    },
    [opened, analytics],
  );

  return (
    <ConfiguratorProvider client={client} designKey={key} bundleUrl={opened.bundleUrl} initialParams={opened.params} onParamsChange={onParamsChange}>
      <WidgetRegistryProvider widgets={brand.widgets} text={widgetText}>
        <Screen opened={opened} defaultsRef={defaultsRef} onOpenVersion={(v) => navigate(`/design/${encodeURIComponent(opened.designId)}?v=${v}`)} />
      </WidgetRegistryProvider>
    </ConfiguratorProvider>
  );
}

function Screen({ opened, defaultsRef, onOpenVersion }: { opened: Opened; defaultsRef: React.MutableRefObject<Record<string, unknown>>; onOpenVersion: (v: string) => void }) {
  const { brand, copy, api, auth, analytics } = useApp();
  const user = useAuthUser(auth);
  const queryClient = useQueryClient();
  const Frame = brand.slots?.ConfiguratorFrame ?? DefaultConfiguratorFrame;
  const { description, status, error } = useDesign();
  const { params, reset, dirty } = useParams();
  const { stats, issues } = useMesh();
  const t = useExtraCopy();
  const { flash, done } = useFlash();
  const [live, setLive] = useState("");
  const focusParam = useFocusParam();
  const nameRef = useRef<HTMLInputElement>(null);
  const saveBlockRef = useRef<HTMLDivElement>(null);
  const exporter = useExport((info) => {
    analytics.track("export", opened.designId, opened.version, { format: info.format, bytes: info.bytes });
    flash(`export-${info.format}`);
    setLive(t("configurator.live.exported", { filename: info.filename, size: `${Math.max(1, Math.round(info.bytes / 1024))} KB` }));
  });
  const migrate = useMigrate();
  const scene = brand.scenes[brand.config.scene];
  const [saveState, setSaveState] = useState<{ busy: boolean; msg: string | null; id?: string; kind?: "draft" | "config" }>({
    busy: false,
    msg: null,
    id: opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.id : opened.saved.config.id) : undefined,
    kind: opened.saved?.kind,
  });
  const [name, setName] = useState(opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft.name : opened.saved.config.name) : "");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const savedFields = opened.saved ? (opened.saved.kind === "draft" ? opened.saved.draft : opened.saved.config) : undefined;
  const [fits, setFits] = useState<FitTagValue[]>(savedFields?.fits ?? []);
  const [printed, setPrinted] = useState(savedFields?.printed ?? false);
  const [notes, setNotes] = useState(savedFields?.notes ?? "");
  const [visibility, setVisibility] = useState<"private" | "public">(opened.saved?.kind === "config" ? opened.saved.config.visibility : "private");
  const [publish, setPublish] = useState<{ busy: boolean; msg: string | null; needsName: boolean }>({ busy: false, msg: null, needsName: false });
  const features = brand.config.features;
  const fitConfig = brand.config.fitTags;
  useEffect(() => {
    if (description) defaultsRef.current = description.defaults;
  }, [description, defaultsRef]);

  const newer = compareSemver(opened.latest, opened.version) > 0 ? opened.latest : null;

  const save = async () => {
    if (!description) return;
    setSaveState((s) => ({ ...s, busy: true, msg: null }));
    const existingNames = (queryClient.getQueryData<SavedConfig[]>(["configs", user?.sub]) ?? []).map((c) => c.name ?? "");
    const title = name.trim() || defaultConfigName(String(description.meta.title ?? description.id), existingNames);
    // An empty notes string clears them on update.
    const tagged = features.fitTags ? { fits, printed, notes: notes.trim() } : {};
    try {
      if (user && api) {
        const cfg =
          saveState.kind === "config" && saveState.id
            ? await api.updateConfig(saveState.id, { name: title, params, version: opened.version, ...tagged })
            : await api.createConfig({ name: title, designId: opened.designId, version: opened.version, params, ...tagged });
        setVisibility(cfg.visibility);
        setSaveState({ busy: false, msg: copy("configurator.saved"), id: cfg.id, kind: "config" });
        window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version: opened.version, params, configId: cfg.id }, defaultsRef.current)}`);
      } else {
        const draft = await saveDraft({ id: saveState.kind === "draft" ? saveState.id : undefined, name: title, designId: opened.designId, version: opened.version, params, brand: brand.config.id, ...tagged });
        setSaveState({ busy: false, msg: auth.enabled ? `${copy("configurator.saved")} · ${copy("auth.required")}` : copy("configurator.saved"), id: draft.id, kind: "draft" });
        window.history.replaceState(null, "", `${window.location.pathname}${buildConfiguratorSearch({ version: opened.version, params, configId: draft.id }, defaultsRef.current)}`);
      }
      setName(title);
      flash("save");
      setLive(copy("configurator.saved"));
      analytics.track("save", opened.designId, opened.version);
    } catch (err) {
      setSaveState((s) => ({ ...s, busy: false, msg: (err as Error).message }));
      setLive((err as Error).message);
    }
  };

  /** Publish the last saved state (or make it private again). Prompts for a display name the first time. */
  const togglePublish = async () => {
    if (!api || saveState.kind !== "config" || !saveState.id) return;
    setPublish({ busy: true, msg: null, needsName: false });
    try {
      const cfg = visibility === "public" ? await api.unpublishConfig(saveState.id) : await api.publishConfig(saveState.id);
      setVisibility(cfg.visibility);
      setPublish({ busy: false, msg: null, needsName: false });
      flash("publish");
      setLive(cfg.visibility === "public" ? t("configurator.done.publish") : t("configurator.done.unpublish"));
    } catch (err) {
      const needsName = err instanceof ApiRequestError && err.body?.code === "display_name_required";
      setPublish({ busy: false, msg: needsName ? null : (err as Error).message, needsName });
    }
  };

  /** Copies the link produced by *this* click (fresh /share/<token> for a saved config), never a stale one. */
  const share = async () => {
    const canToken = !!(api && user && saveState.kind === "config" && saveState.id);
    let url: string;
    try {
      ({ url } = await resolveShareUrl({
        origin: window.location.origin,
        currentHref: window.location.href,
        createToken: canToken ? async () => (await api!.shareConfig(saveState.id!)).token : undefined,
      }));
    } catch (err) {
      setLive((err as Error).message);
      return;
    }
    setShareUrl(url);
    analytics.track("share", opened.designId, opened.version);
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      flash("share");
      setLive(t("configurator.live.linkCopied", { url }));
    } catch {
      // clipboard may be unavailable: the link is shown below to copy by hand
      setShareCopied(false);
      setLive(t("configurator.live.linkReady", { url }));
    }
  };

  const upgrade = async () => {
    if (!newer) return;
    // Migrate the current params in the worker (design.migrateFrom), then open the
    // newer version with them; saving afterwards writes a new revision, never in place.
    const client = getKernelClient();
    const target = await registryResolve(newer);
    if (target) await client.load(`${opened.designId}@${newer}`, target.bundleUrl);
    const migrated = await client.migrate(`${opened.designId}@${newer}`, opened.version, params).catch(() => params);
    sessionStorage.setItem(`cfg-migrated:${opened.designId}@${newer}`, JSON.stringify(migrated));
    onOpenVersion(newer);
  };
  const { registry } = useApp();
  const registryResolve = (v: string) => registry.resolve(opened.designId, v);
  void migrate;

  const title = description ? displayTitle(String(description.meta.title ?? description.id)) : opened.designId;
  const printNotes = description ? (description.meta.printNotes as string | undefined) : undefined;

  // Export is disabled only by real blockers; a click during a rebuild waits for it ("Preparing…").
  const blocking = issues.filter((i) => i.level === "error");
  const exportDisabled = !description || status === "error" || !!exporter.busy || blocking.length > 0;
  const exportLabel = (format: "stl" | "3mf", idle: string) =>
    exporter.busy === format ? (exporter.phase === "preparing" ? t("configurator.preparing") : t("configurator.exporting")) : done === `export-${format}` ? t("configurator.done.export") : idle;
  const btnState = (id: string, busy?: boolean) => `${done === id ? " is-done" : ""}${busy ? " is-busy" : ""}`;
  useEffect(() => {
    if (exporter.blocked) setLive(t(blocking.length === 1 ? "configurator.blocked.one" : "configurator.blocked.other", { count: blocking.length }).replace(/\s*↑$/, ""));
    else if (exporter.error) setLive(exporter.error);
  }, [exporter.blocked, exporter.error]);

  /** "Fix N print issues ↑": reveal the first offending field, or the issue list. */
  const showBlocking = () => {
    const withParam = blocking.find((i) => i.param);
    if (withParam?.param) return focusParam(withParam.param);
    const list = document.getElementById("cfg-issues");
    list?.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
    list?.focus({ preventScroll: true });
  };
  /** "Save ↓": bring the save block into view and focus the name field. */
  const jumpToSave = () => {
    saveBlockRef.current?.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
    nameRef.current?.focus({ preventScroll: true });
  };

  const sizeText = stats ? `${(stats.bbox.max[0] - stats.bbox.min[0]).toFixed(1)} × ${(stats.bbox.max[1] - stats.bbox.min[1]).toFixed(1)} × ${(stats.bbox.max[2] - stats.bbox.min[2]).toFixed(1)} mm` : "";

  return (
    <section className="page page-configurator">
      <header className="page-head configurator-head">
        <div>
          <h1>{title}</h1>
          {description?.meta.description ? <p className="page-sub">{String(description.meta.description)}</p> : null}
        </div>
        <div className="configurator-meta">
          <span className="version-pill" title="Design version pinned for this configuration">
            v{opened.version}
          </span>
          {opened.deprecated && <span className="version-pill is-warn">deprecated</span>}
          {newer && brand.config.features.saveConfigs && (
            <button type="button" className="cfg-btn" onClick={upgrade}>
              {copy("configurator.update", { version: newer })}
            </button>
          )}
        </div>
      </header>

      <Frame
        aside={
          printNotes ? (
            <div className="print-notes">
              <strong>Print notes.</strong> {printNotes}
            </div>
          ) : undefined
        }
      >
        <div className="configurator-layout">
          <div className="configurator-stage">
            <Viewport
              preset={scene}
              modelColor={brand.config.theme.colors.model}
              showLabels={brand.config.features.floatingLabels}
              label={t("configurator.viewport", { title })}
              busyLabel={t("configurator.busy")}
            />
            <div className="stage-stats">
              {stats ? (
                <>
                  <StatChip kind="size" value={sizeText} />
                  <StatChip kind="volume" value={`${(stats.volume / 1000).toFixed(1)} cm³`} />
                  <span className="stage-stat stage-stat-telemetry">
                    {stats.numTri.toLocaleString()} tris · {stats.ms.toFixed(0)} ms
                  </span>
                </>
              ) : (
                <span>{status === "error" ? error : copy("configurator.building")}</span>
              )}
            </div>
          </div>

          <aside className="configurator-panel">
            <div className="panel-title-row">
              <h2>{copy("configurator.params")}</h2>
              <button type="button" className="cfg-chip" onClick={reset} disabled={!dirty && !opened.saved}>
                {copy("configurator.reset")}
              </button>
            </div>
            {description ? (
              <>
                <PresetBar label={copy("configurator.presets")} />
                <ParamForm />
              </>
            ) : (
              <PanelSkeleton />
            )}
            <IssueList title={copy("configurator.warnings")} />

            <div className="panel-actions" data-blocked={blocking.length > 0 || undefined}>
              {brand.config.features.exportStl && (
                <button
                  type="button"
                  className={`cfg-btn cfg-btn-primary cfg-btn-block${btnState("export-stl", exporter.busy === "stl")}`}
                  disabled={exportDisabled}
                  aria-describedby={blocking.length ? "cfg-actions-reason" : undefined}
                  data-phase={exporter.busy === "stl" ? exporter.phase : undefined}
                  onClick={exporter.exportStl}
                >
                  {exportLabel("stl", copy("configurator.export.stl"))}
                </button>
              )}
              {brand.config.features.export3mf && (
                <button
                  type="button"
                  className={`cfg-btn cfg-btn-block${btnState("export-3mf", exporter.busy === "3mf")}`}
                  disabled={exportDisabled}
                  aria-describedby={blocking.length ? "cfg-actions-reason" : undefined}
                  data-phase={exporter.busy === "3mf" ? exporter.phase : undefined}
                  onClick={exporter.export3mf}
                >
                  {exportLabel("3mf", copy("configurator.export.3mf"))}
                </button>
              )}
              {blocking.length > 0 && (
                <p className="cfg-actions-reason" id="cfg-actions-reason">
                  <button type="button" onClick={showBlocking}>
                    {t(blocking.length === 1 ? "configurator.blocked.one" : "configurator.blocked.other", { count: blocking.length })}
                  </button>
                </p>
              )}
              {brand.config.features.saveConfigs && (
                <button type="button" className="cfg-text-btn cfg-save-jump" onClick={jumpToSave}>
                  {t("configurator.saveJump")}
                </button>
              )}
              <p className="cfg-live" role="status" aria-live="polite">
                {live}
              </p>
            </div>

            {brand.config.features.saveConfigs && (
              <div className="panel-save" ref={saveBlockRef} role="group" aria-labelledby="cfg-save-heading">
                <h3 className="panel-save-title" id="cfg-save-heading">
                  {t("configurator.saveHeading")}
                </h3>
                <label className="cfg-label panel-save-label" htmlFor="cfg-config-name">
                  {t("configurator.nameLabel")}
                </label>
                <input
                  ref={nameRef}
                  id="cfg-config-name"
                  className="cfg-input cfg-config-name"
                  placeholder={t("configurator.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {features.fitTags && (
                  <>
                    <FitTagInput
                      value={fits}
                      onChange={setFits}
                      label={fitConfig.label}
                      pattern={fitConfig.refPattern}
                      refPlaceholder={fitConfig.refPlaceholder}
                      namePlaceholder={fitConfig.namePlaceholder}
                      addLabel={copy("fits.add")}
                      messages={{ invalid: copy("fits.invalid"), max: copy("fits.max", { max: 10 }) }}
                    />
                    <label className="cfg-check">
                      <input type="checkbox" checked={printed} onChange={(e) => setPrinted(e.target.checked)} /> {copy("fits.printed")}
                    </label>
                    <label className="cfg-label panel-save-label" htmlFor="cfg-config-notes">
                      {t("configurator.notesLabel")}
                    </label>
                    <textarea
                      id="cfg-config-notes"
                      className="cfg-input cfg-textarea"
                      rows={2}
                      maxLength={1000}
                      placeholder={copy("fits.notes")}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </>
                )}
                <div className="panel-save-row">
                  <button type="button" className={`cfg-btn cfg-save-btn${btnState("save", saveState.busy)}`} disabled={saveState.busy || !description} onClick={save}>
                    {saveState.busy ? t("configurator.saving") : done === "save" ? t("configurator.done.save") : copy("configurator.save")}
                  </button>
                  {brand.config.features.shareLinks && (
                    <button type="button" className={`cfg-btn cfg-share-btn${btnState("share")}`} onClick={share} disabled={!description}>
                      {done === "share" ? t("configurator.done.share") : copy("configurator.share")}
                    </button>
                  )}
                </div>
                {saveState.msg && <p className="muted small">{saveState.msg}</p>}
                {features.publicGallery && user && api && (
                  <div className="panel-publish">
                    {saveState.kind === "config" && saveState.id ? (
                      <div className="panel-save-row">
                        <button type="button" className={`cfg-btn cfg-publish-btn${btnState("publish", publish.busy)}`} disabled={publish.busy} onClick={togglePublish}>
                          {done === "publish"
                            ? visibility === "public"
                              ? t("configurator.done.publish")
                              : t("configurator.done.unpublish")
                            : visibility === "public"
                              ? copy("community.unpublish")
                              : copy("community.publish")}
                        </button>
                        {visibility === "public" && (
                          <a className="version-pill" href={`/community/${encodeURIComponent(saveState.id)}`}>
                            {copy("community.public")}
                          </a>
                        )}
                      </div>
                    ) : (
                      <p className="muted small">{copy("community.publishHint")}</p>
                    )}
                    {publish.needsName && <DisplayNameForm onDone={() => void togglePublish()} />}
                    {publish.msg && <p className="cfg-error">{publish.msg}</p>}
                  </div>
                )}
                {shareUrl && (
                  <p className="muted small share-url">
                    <a href={shareUrl}>{shareUrl}</a>
                    {shareCopied ? " (copied)" : ""}
                  </p>
                )}
                {!user && auth.enabled && (
                  <p className="muted small">
                    {copy("auth.required")}{" "}
                    <button type="button" className="link" onClick={() => void auth.signIn()}>
                      {copy("auth.signIn")}
                    </button>
                  </p>
                )}
              </div>
            )}
          </aside>
        </div>
      </Frame>
    </section>
  );
}
